import {
    SINGLE_INSTANCE_KEYS,
    USER_ACCOUNT_EMAIL_KW,
    USER_ACCOUNT_NAME_KW,
} from "../../components/submitApplication/single/recognisedInputs/recognisedInputKeys"
import { IDeviceClassDispatcherType } from "../../components/submitApplication/single/types/formGeneratorTypes"
import {
    ApplicationClasses,
    Datasets,
    ExistingDeviceClasses,
    InstallerApplicationStatuses,
    ProposedDeviceClasses,
    recognisedLctDevices,
    VALID_DEVICE_CLASSES_FOR_ADMD_VALIDATION,
} from "../../constants/enums"
import {
    BATTERY,
    BATTERY_DISPLAY_NAME,
    EVAC_DISPLAY_NAME,
    EVCP_AC,
    EVCP_DC,
    EVDC_DISPLAY_NAME,
    EV_V2G,
    HEAT_PUMP,
    ELECTRIC_BOILER,
    AIR_CONDITIONING_UNIT,
    HP_DISPLAY_NAME,
    EB_DISPLAY_NAME,
    ACU_DISPLAY_NAME,
    SOLAR_PV,
    SOLAR_PV_DISPLAY_NAME,
    V2G_INVERTER_DISPLAY_NAME,
} from "../../constants/generalConstants"
import { ISingleApplicationDetail } from "../../services/middleware/mappers/dnoInterface/dnoApplicationsMapper"
import { IApplication } from "../../services/middleware/types/installerInterface/installerApplicationTypes"
import {
    IFormProperties,
    IBasicInputSpec,
} from "../../services/middleware/types/singleApplication/getSchemaTypes"

/**
 * Prepares form properties for mapping into the InputGenerator. Removes
 * any duplicate form properties that are 1) recognised inputs and 2)
 * are in the subset of recognised inputs that should only be rendered once.
 * @param formProperties - The form properties to prepare
 * @returns The prepared form properties
 */
export const prepareFormProperties = (
    formProperties: IFormProperties
): Array<[string, IBasicInputSpec]> =>
    Object.entries(formProperties).reduce(
        (
            acc: Array<[string, IBasicInputSpec]>,
            [k, v]: [string, IBasicInputSpec]
        ) => {
            // Remove duplicated recognised inputs
            if (SINGLE_INSTANCE_KEYS.includes(v.json_schema_extra.type)) {
                if (
                    !acc.some(
                        ([, val]: [string, IBasicInputSpec]) =>
                            val.json_schema_extra.type ===
                            v.json_schema_extra.type
                    )
                ) {
                    acc.push([k, v])
                }
            } else {
                acc.push([k, v])
            }
            return acc
        },
        []
    )

/**
 * Scrolls to the top of the page.
 * Ref: https://www.w3schools.com/howto/howto_js_scroll_to_top.asp
 * @param document {Document} - The document object
 */
export const scrollToTop = (document: Document): void => {
    // eslint-disable-next-line no-param-reassign
    document.body.scrollTop = 0 // For Safari
    // eslint-disable-next-line no-param-reassign
    document.documentElement.scrollTop = 0 // For Chrome, Firefox, IE and Opera
}

export const scrollToBottom = (document: Document): void => {
    // eslint-disable-next-line no-param-reassign
    document.body.scrollTop = document.body.scrollHeight // For Safari
    // eslint-disable-next-line no-param-reassign
    document.documentElement.scrollTop = document.documentElement.scrollHeight // For Chrome, Firefox, IE and Opera
}

export const IN_DATABASE_KEY = "inDatabase"
export const NOT_IN_DATABASE_KEY = "notInDatabase"
export const EXISTING_IN_DATABASE_KEY = "existingInDatabase"
export const EXISTING_NOT_IN_DATABASE_KEY = "existingNotInDatabase"
export const SELECT_MANUAL_OPTION_NEW = "isSelectManualOptionNew"
export const SELECT_MANUAL_OPTION_EXISTING = "isSelectManualOptionExisting"

// Map high level technology types to device types
const lctDeviceTypeDispatcher = {
    [SOLAR_PV]: [recognisedLctDevices.solarPv],
    [EVCP_AC]: [recognisedLctDevices.evcpAc],
    [EVCP_DC]: [recognisedLctDevices.evcpDc],
    // PBI 71845 — [EV_V2G] dropped recognisedLctDevices.v2gInverter; the
    // V2G_INVERTER is now auto-resolved server-side from the linked EVCP_AC's
    // V2G_TTRStatus. Safe today because there are no V2G applications in
    // flight (drafts/amendments). When the amend flow is reintroduced, pre-
    // 3.1.0 saved schemas may still contain a V2G_INVERTER row and will need
    // handling on load — see backlog item for legacy-V2G migration.
    [EV_V2G]: [recognisedLctDevices.evcpAc],
    [HEAT_PUMP]: [recognisedLctDevices.hp],
    [ELECTRIC_BOILER]: [recognisedLctDevices.eb],
    [AIR_CONDITIONING_UNIT]: [recognisedLctDevices.acu],
    [BATTERY]: [recognisedLctDevices.battery],
}

/**
 * PBI 71845 — returns true when a device row is the EVCP_AC slot of a V2G
 * application. Used to gate three V2G-specific surfaces:
 *   - filtering unlinked EVCPs out of the picker (selectNewDevice)
 *   - hiding manual-entry for that row (selectDeviceForm)
 *   - rendering the auto-resolved-inverter alert (selectDeviceForm)
 * Centralised so the surfaces cannot drift out of sync.
 */
export const isV2gEvcpContext = (opts: {
    lcts: string[] | undefined
    dataset: Datasets | undefined
}): boolean => {
    const { lcts, dataset } = opts
    return Boolean(lcts?.includes(EV_V2G)) && dataset === Datasets.EVAC
}

// Map devices to device properties
export const deviceClassDispatcher: IDeviceClassDispatcherType = {
    [recognisedLctDevices.hp]: {
        displayName: HP_DISPLAY_NAME,
        databaseName: Datasets.HP,
        [SELECT_MANUAL_OPTION_NEW]: true,
        [SELECT_MANUAL_OPTION_EXISTING]: true,
        [IN_DATABASE_KEY]: ProposedDeviceClasses.hpRegistered,
        [NOT_IN_DATABASE_KEY]: ProposedDeviceClasses.hpNotRegistered,
        [EXISTING_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingDemandRegistered,
        [EXISTING_NOT_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingDemandNotRegistered,
    },
    [recognisedLctDevices.eb]: {
        displayName: EB_DISPLAY_NAME,
        databaseName: Datasets.EB,
        [SELECT_MANUAL_OPTION_NEW]: true,
        [SELECT_MANUAL_OPTION_EXISTING]: true,
        [IN_DATABASE_KEY]: ProposedDeviceClasses.ebRegistered,
        [NOT_IN_DATABASE_KEY]: ProposedDeviceClasses.ebNotRegistered,
        [EXISTING_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingDemandRegistered,
        [EXISTING_NOT_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingDemandNotRegistered,
    },
    [recognisedLctDevices.acu]: {
        displayName: ACU_DISPLAY_NAME,
        databaseName: Datasets.ACU,
        [SELECT_MANUAL_OPTION_NEW]: true,
        [SELECT_MANUAL_OPTION_EXISTING]: true,
        [IN_DATABASE_KEY]: ProposedDeviceClasses.acuRegistered,
        [NOT_IN_DATABASE_KEY]: ProposedDeviceClasses.acuNotRegistered,
        [EXISTING_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingDemandRegistered,
        [EXISTING_NOT_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingDemandNotRegistered,
    },
    [recognisedLctDevices.evcpAc]: {
        displayName: EVAC_DISPLAY_NAME,
        databaseName: Datasets.EVAC,
        [SELECT_MANUAL_OPTION_NEW]: true,
        [SELECT_MANUAL_OPTION_EXISTING]: true,
        [IN_DATABASE_KEY]: ProposedDeviceClasses.demandGeneralRegistered,
        [NOT_IN_DATABASE_KEY]: ProposedDeviceClasses.demandGeneralNotRegistered,
        [EXISTING_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingDemandRegistered,
        [EXISTING_NOT_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingDemandNotRegistered,
    },
    [recognisedLctDevices.evcpDc]: {
        displayName: EVDC_DISPLAY_NAME,
        databaseName: Datasets.EVDC,
        [SELECT_MANUAL_OPTION_NEW]: true,
        [SELECT_MANUAL_OPTION_EXISTING]: true,
        [IN_DATABASE_KEY]: ProposedDeviceClasses.demandGeneralRegistered,
        [NOT_IN_DATABASE_KEY]: ProposedDeviceClasses.demandGeneralNotRegistered,
        [EXISTING_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingDemandRegistered,
        [EXISTING_NOT_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingDemandNotRegistered,
    },
    [recognisedLctDevices.battery]: {
        displayName: BATTERY_DISPLAY_NAME,
        databaseName: Datasets.TTR,
        [SELECT_MANUAL_OPTION_NEW]: false,
        [SELECT_MANUAL_OPTION_EXISTING]: true,
        [IN_DATABASE_KEY]: ProposedDeviceClasses.genGeneral,
        [NOT_IN_DATABASE_KEY]: ProposedDeviceClasses.genGeneral,
        [EXISTING_IN_DATABASE_KEY]: ExistingDeviceClasses.existingGenRegistered,
        [EXISTING_NOT_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingGenNotRegistered,
    },
    [recognisedLctDevices.v2gInverter]: {
        displayName: V2G_INVERTER_DISPLAY_NAME,
        databaseName: Datasets.TTR,
        [SELECT_MANUAL_OPTION_NEW]: false,
        [SELECT_MANUAL_OPTION_EXISTING]: true,
        [IN_DATABASE_KEY]: ProposedDeviceClasses.genGeneral,
        [NOT_IN_DATABASE_KEY]: ProposedDeviceClasses.genGeneral,
        [EXISTING_IN_DATABASE_KEY]: ExistingDeviceClasses.existingGenRegistered,
        [EXISTING_NOT_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingGenNotRegistered,
    },
    [recognisedLctDevices.solarPv]: {
        displayName: SOLAR_PV_DISPLAY_NAME,
        databaseName: Datasets.TTR,
        [SELECT_MANUAL_OPTION_NEW]: false,
        [SELECT_MANUAL_OPTION_EXISTING]: true,
        [IN_DATABASE_KEY]: ProposedDeviceClasses.genGeneral,
        [NOT_IN_DATABASE_KEY]: ProposedDeviceClasses.genGeneral,
        [EXISTING_IN_DATABASE_KEY]: ExistingDeviceClasses.existingGenRegistered,
        [EXISTING_NOT_IN_DATABASE_KEY]:
            ExistingDeviceClasses.existingGenNotRegistered,
    },
}

/**
 * Generates device class dispatcher based on the proposed classes.
 * @param {string[]} proposedClasses - An array of classes from `ProposedDeviceClasses`.
 * @returns {Record<string, IDeviceClassDispatcherType>} An object where each key is a string and each value is of type `DeviceClassDispatcherType`.
 */
export const buildDeviceClassDispatcher = (
    proposedClasses: string[],
    newDeviceMode: boolean
): IDeviceClassDispatcherType =>
    Object.entries(deviceClassDispatcher).reduce((result, [key, value]) => {
        const inDatabaseClass = newDeviceMode
            ? value.inDatabase
            : value.existingInDatabase
        const notInDatabaseClass = newDeviceMode
            ? value.notInDatabase
            : value.existingNotInDatabase
        if (
            proposedClasses.includes(inDatabaseClass) ||
            proposedClasses.includes(notInDatabaseClass)
        ) {
            return {
                ...result,
                [key]: value,
            }
        }
        return result
    }, {})

/**
 * This gets to allowed device types based on an array of device classes
 * @param {string[]} proposedClasses - An array of classes from `ProposedDeviceClasses`.
 * @returns {string[]} An array of permitted recognisedLcts.
 */
export const getDeviceTypesFromClasses = (
    proposedClasses: string[],
    newDeviceMode: boolean
): string[] =>
    Object.entries(deviceClassDispatcher)
        .filter(([, value]) => {
            const inDatabaseClass = newDeviceMode
                ? value.inDatabase
                : value.existingInDatabase
            const notInDatabaseClass = newDeviceMode
                ? value.notInDatabase
                : value.existingNotInDatabase

            return (
                proposedClasses.includes(inDatabaseClass) ||
                proposedClasses.includes(notInDatabaseClass)
            )
        })
        .map(([key]) => key)

/**
 * This gets the key of a device class based on the display name
 * @param {string} displayName - The display name of the device class
 * @returns {string | undefined} The key of the device class
 */
export const getKeyByDisplayName = (
    displayName: string
): string | undefined => {
    const entry = Object.entries(deviceClassDispatcher).find(
        ([, value]) => value.displayName === displayName
    )
    return entry ? entry[0] : undefined
}

/**
 * Injected values from the user account into the form. This is for use
 * for inputs with the type USER ACCOUNT DERIVED INPUT
 * @param {object} acc - The accumulator object (this will be used in a reduce)
 * @param {string} key - The key of the input
 * @param {IBasicInputSpec} value - The value of the input fork spec
 * @param {string} email - The email of the user
 * @param {string} firstName - The first name of the user
 * @param {string} lastName - The last name of the user
 * @returns {object} The new accumulator object to which the derived input
 * gets injected.
 */
export const processUserAccountDerivedInputs = (
    acc: object,
    key: string,
    value: IBasicInputSpec,
    email: string,
    firstName: string,
    lastName: string
): any => {
    if (value.json_schema_extra.fixedValue === USER_ACCOUNT_EMAIL_KW) {
        return {
            ...acc,
            [key]: email,
        }
    }
    if (value.json_schema_extra.fixedValue === USER_ACCOUNT_NAME_KW) {
        return {
            ...acc,
            [key]: `${firstName} ${lastName}`,
        }
    }
    return acc
}

/**
 * This gets the key of a device class based on the display name
 * @param {string} displayName - The display name of the device class
 * @returns {string | null} The key of the device class
 */
export const getDeviceTypeKeyByDisplayName = (
    displayName: string
): string | null => {
    const keys = Object.keys(deviceClassDispatcher)
    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i]
        if (deviceClassDispatcher[key].displayName === displayName) {
            return key
        }
    }
    return null
}

/**
 * This gets the display name of a device class based on the key
 * @param {string[]} lctList - Input list of LCTs
 * @returns {string[]} - array of device types for the selected LCTs
 */
export const getDeviceTypesForSelectedLcts = (lctList: string[]): string[] =>
    lctList.reduce(
        (mergedList, lct) =>
            lctDeviceTypeDispatcher[lct as keyof typeof lctDeviceTypeDispatcher]
                ? [
                      ...mergedList,
                      ...lctDeviceTypeDispatcher[
                          lct as keyof typeof lctDeviceTypeDispatcher
                      ],
                  ]
                : mergedList,
        [] as string[]
    )

/**
 * Removes any key-value pairs from an object which are of type array or object.
 * @param obj - The input object
 * @returns The resulting object
 */
export const stripArraysAndObjectValues = <T extends Record<string, unknown>>(
    obj: T
): Partial<T> =>
    Object.fromEntries(
        Object.entries(obj).filter(([, value]) => typeof value !== "object")
    ) as Partial<T>

/**
 * Converts an array of objects into an object, for use in in application
 * review and submit page for devices and devices to install
 * @param array - The array of objects
 * @returns The resulting object
 */
export const createDevicesNestedObj = <T extends Record<string, unknown>>(
    array: Array<Partial<T>>
): Record<string, Partial<T>> =>
    array.reduce<Record<string, Partial<T>>>(
        (obj, item, index) => ({
            ...obj,
            [`Device ${index + 1}`]: stripArraysAndObjectValues(item),
        }),
        {}
    )

export const applicationIsAdmdCompliant = (
    applicationClass: ApplicationClasses,
    existingDevices: Record<string, any>[],
    devicesToInstall: Record<string, any>[],
    maxDemand: number,
    importLimitingDevicePresent: boolean
): boolean => {
    try {
        if (applicationClass === ApplicationClasses.genGeneral) {
            return true
        }

        if (importLimitingDevicePresent) {
            return true
        }

        const relevantDevices = existingDevices
            .filter((device) => !device.isBeingRemoved || false)
            .concat(devicesToInstall)

        const totalDemand = relevantDevices
            .filter((device) =>
                VALID_DEVICE_CLASSES_FOR_ADMD_VALIDATION.includes(
                    device.deviceClass as
                        | ProposedDeviceClasses
                        | ExistingDeviceClasses
                )
            )
            .reduce<number>(
                (acc, device) =>
                    (acc +
                        (device.supplementingInfo?.deviceSysRefSearchResult
                            ?.deviceSize ??
                            parseFloat(
                                device.deviceMaxCurrentDemand
                            ))) as number,
                0
            )

        return totalDemand <= maxDemand
    } catch (err) {
        return true
    }
}
// check if application has generation device
export const applicationHasGenerationDevice = (
    application: IApplication | ISingleApplicationDetail
): boolean => {
    if (
        application.applicationClass === ApplicationClasses.genGeneral ||
        application.applicationClass === ApplicationClasses.genDemandGeneral
    ) {
        return true
    }
    return false
}

/**
 * Creates user friendly text for the amendment failure alert
 */
export const mapAmendFailureText = (
    amendmentFailureCategory: string,
    dno: string
): {
    title: string
    subtitle: string
    whatHappensNext: string
    installerSingleAppAlert: string
} => {
    switch (amendmentFailureCategory) {
        case InstallerApplicationStatuses.failedToLarge:
            return {
                title: "Your amendment could not be processed",
                subtitle:
                    "Unfortunately, your amendment could not be processed as the devices you have selected are too large for ENA Connect Direct",
                whatHappensNext: `We have cancelled the amendment and reverted your application back to its previous state. 
                The application is still open and active. ${dno} has not been notified of this attempted amendment.`,
                installerSingleAppAlert:
                    " the devices you selected are too large for ENA Connect Direct. We cancelled the amendment and reverted your application back to its previous state. The application is still open and active",
            }
        case InstallerApplicationStatuses.invalidCutOut:
            return {
                title: "Your amendment could not be processed",
                subtitle:
                    "Unfortunately, your amendment could not be processed as we were unable to detect a cut-out in the image you provided",
                whatHappensNext: `We have cancelled the amendment and reverted your application back to its previous state. 
                The application is still open and active. ${dno} has not been notified of this attempted amendment. 
                Please try again with another image. To maximise chances of success, please ensure it is taken 
                within 1m of the cut-out and is in focus`,
                installerSingleAppAlert:
                    " we were unable to detect a cut-out in the image you provided. We cancelled the amendment and reverted your application back to its previous state. The application is still open and active. Please try again with another image. To maximise chances of success, please ensure it is taken within 1m of the cut-out and is in focus",
            }

        case InstallerApplicationStatuses.imageScanFailed:
            return {
                title: "Your amendment could not be processed",
                subtitle:
                    "Unfortunately, your amendment could not be processed as the image you provided could not be processed",
                whatHappensNext: `We have cancelled the amendment and reverted your application back to its previous state. 
                The application is still open and active. ${dno} has not been notified of this attempted amendment. 
                Please try again with another image. To maximise chances of success, ensure it is a JPEG or PNG image. 
                Do not submit a PDF or other file type`,
                installerSingleAppAlert:
                    " the image you provided could not be processed. We cancelled the amendment and reverted your application back to its previous state. The application is still open and active. Please try again with another image. To maximise chances of success, ensure it is a JPEG or PNG image. Do not submit a PDF or other file type",
            }

        default:
            return {
                title: "Your amendment could not be processed",
                subtitle:
                    "Unfortunately, something went wrong during the processing of you amendment",
                whatHappensNext: `We have cancelled the amendment and reverted your application back to its previous state. 
                The application is still open and active. ${dno} has not been notified of this attempted amendment. 
                Please try again, and if this issue occurs for a second time, please contact our support team`,
                installerSingleAppAlert:
                    " an error occurred during its re-assessment. We cancelled the amendment and reverted your application back to its previous state. The application is still open and active. Please try again, and if this issue occurs for a second time, please contact our support team",
            }
    }
}
